import { useEffect, useRef, useState } from 'react'
import { useDateRangeCtx, type RangePreset } from './DateRangeContext'

const LABELS: Record<Exclude<RangePreset, 'custom'>, string> = {
  '24h': '24 timer',
  '7d': '7 dage',
  '30d': '30 dage',
  '90d': '90 dage',
}

function toDateInput(ms: number): string {
  const d = new Date(ms)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function fromDateInput(s: string, endOfDay = false): number {
  const [y, m, d] = s.split('-').map((n) => parseInt(n, 10))
  if (!y || !m || !d) return Date.now()
  const date = new Date(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0)
  return date.getTime()
}

function formatRangeLabel(from: number, to: number): string {
  const f = new Date(from)
  const t = new Date(to)
  const fmt = (d: Date) => `${d.getDate()}/${d.getMonth() + 1}`
  return `${fmt(f)} – ${fmt(t)}`
}

export function DateRangeTabs() {
  const { preset, setPreset, customFrom, customTo, setCustom } = useDateRangeCtx()
  const [open, setOpen] = useState(false)
  const [draftFrom, setDraftFrom] = useState(toDateInput(customFrom))
  const [draftTo, setDraftTo] = useState(toDateInput(customTo))
  const popoverRef = useRef<HTMLDivElement>(null)

  // Close popover on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const apply = () => {
    const a = fromDateInput(draftFrom, false)
    const b = fromDateInput(draftTo, true)
    // Auto-swap if user pasted dates in wrong order — never silently fail.
    const from = Math.min(a, b)
    const to = Math.max(a, b)
    setCustom(from, to)
    setOpen(false)
  }

  return (
    <div className="time-tabs">
      {(['24h', '7d', '30d', '90d'] as const).map((p) => (
        <button
          key={p}
          className={`time-tab ${preset === p ? 'active' : ''}`}
          onClick={() => setPreset(p)}
        >
          {LABELS[p]}
        </button>
      ))}
      <div className="custom-range-wrap" ref={popoverRef}>
        <button
          className={`time-tab ${preset === 'custom' ? 'active' : ''}`}
          onClick={() => {
            setDraftFrom(toDateInput(customFrom))
            setDraftTo(toDateInput(customTo))
            setOpen((o) => !o)
          }}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          {preset === 'custom' ? formatRangeLabel(customFrom, customTo) : 'Tilpasset…'}
        </button>
        {open && (
          <div className="custom-range-popover" role="dialog">
            <div className="custom-range-row">
              <label>
                <span className="custom-range-label">Fra</span>
                <input type="date" value={draftFrom} max={draftTo} onChange={(e) => setDraftFrom(e.target.value)} />
              </label>
              <label>
                <span className="custom-range-label">Til</span>
                <input type="date" value={draftTo} min={draftFrom} onChange={(e) => setDraftTo(e.target.value)} />
              </label>
            </div>
            <div className="custom-range-actions">
              <button className="tab-pill" onClick={() => setOpen(false)}>Annuller</button>
              <button className="btn-action" onClick={apply}>Anvend</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
